// =============================================================================
// teamQuotes — RPC wrappers for the Teamuitjes section (work item team-uitjes)
// =============================================================================
// booking.fn_admin_team_quotes / fn_admin_team_quote_update, both gated by
// booking_team_outings in the database. Creating and confirming by hand go
// through the booking-team edge function (it sends the customer mails).
import { BOOKING_API_URL, supabase } from './supabase';

export type TeamQuoteStatus =
  | 'requested'
  | 'accepted'
  | 'invoiced'
  | 'paid'
  | 'expired'
  | 'cancelled';

export interface TeamQuote {
  id: string;
  quote_number: string;
  status: TeamQuoteStatus;
  locale: 'nl' | 'en';
  company: string;
  contact_name: string;
  email: string;
  invoice_email: string | null;
  phone: string;
  street: string;
  postcode: string;
  city: string;
  vat_number: string | null;
  reference: string | null;
  notes: string | null;
  location: { id: string; name: string };
  start_at: string;
  end_at: string;
  parts: Array<{ slot_start: string; people: number }>;
  group_size: number;
  slot_count: number;
  places_per_slot: number;
  price_per_slot_ex_cents: number;
  total_ex_cents: number;
  vat_cents: number;
  total_inc_cents: number;
  vat_rate_pct: number;
  expires_at: string;
  visit_date: string;
  invoice_date: string | null;
  pay_by_date: string | null;
  reminder_sent_at: string | null;
  accepted_at: string | null;
  // Operator who confirmed it in the admin; null when the customer accepted.
  accepted_by?: string | null;
  expired_at: string | null;
  invoiced_at: string | null;
  invoiced_by?: string | null;
  paid_at: string | null;
  paid_by?: string | null;
  cancelled_at: string | null;
  cancelled_by?: string | null;
  // Who did what: 'created' / 'confirmed' by staff, 'invoiced' then every
  // 'invoice_date' change, 'declined' by the customer.
  history?: Array<{
    at: string;
    by: string | null;
    what: 'created' | 'confirmed' | 'invoiced' | 'invoice_date' | 'declined';
    invoice_date?: string;
    was?: string | null;
  }>;
  cancel_reason: string | null;
  staff_notes: string | null;
  mail_log: Array<{ kind: string; ok: boolean; error: string | null; at: string }>;
  created_at: string;
  // Operator who made it by hand in Teamuitjes; null for a /teams request.
  created_by?: string | null;
  // False: staff chose "Geen mail naar klant".
  mail_customer?: boolean;
  booking_id: string;
  booking_number: string;
}

export const TEAM_STATUS_LABEL: Record<TeamQuoteStatus, string> = {
  requested: 'Aangevraagd',
  accepted:  'Geaccepteerd',
  invoiced:  'Gefactureerd',
  paid:      'Betaald',
  expired:   'Verlopen',
  cancelled: 'Geannuleerd',
};

export const TEAM_STATUS_CLASS: Record<TeamQuoteStatus, string> = {
  requested: 'voucher-status-warn',
  accepted:  'voucher-status-ok',
  invoiced:  'voucher-status-ok',
  paid:      'voucher-status-ok',
  expired:   'voucher-status-off',
  cancelled: 'voucher-status-off',
};

export async function listTeamQuotes(
  locationId: string | null,
  status: string | null,
): Promise<TeamQuote[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_team_quotes', {
      p_location_id: locationId,
      p_status: status,
    });
  if (error) throw error;
  return (data ?? []) as TeamQuote[];
}

export type TeamQuoteAction = 'invoiced' | 'paid' | 'cancelled' | 'notes';

export async function updateTeamQuote(
  id: string,
  action: TeamQuoteAction,
  opts: { invoiceDate?: string | null; reason?: string | null; staffNotes?: string | null } = {},
): Promise<TeamQuote> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_team_quote_update', {
      p_id: id,
      p_action: action,
      p_invoice_date: opts.invoiceDate ?? null,
      p_reason: opts.reason ?? null,
      p_staff_notes: opts.staffNotes ?? null,
    });
  if (error) throw error;
  return data as TeamQuote;
}

// -----------------------------------------------------------------------------
// Create / confirm by hand (booking-team admin_create / admin_accept)
// -----------------------------------------------------------------------------
export interface TeamLocationConfig {
  location_id: string;
  name: string;
  capacity_per_slot: number;
  price_per_slot_ex_cents: number;
  vat_rate_pct: number;
  min_days_ahead: number;
  min_group: number;
  max_group: number;
  quote_hours: number;
}

export interface TeamSlot {
  slot_start: string;
  room_id: string;
  capacity: number;
  free: boolean;
}

export interface TeamQuoteInput {
  location_id: string;
  date: string;   // YYYY-MM-DD
  start: string;  // HH:MM
  group_size: number;
  company: string;
  contact_name: string;
  email: string;
  phone: string;
  invoice_email: string;
  street: string;
  postcode: string;
  city: string;
  vat_number: string;
  reference: string;
  notes: string;
  locale: 'nl' | 'en';
  price_per_slot_ex_cents: number;
  mail_customer: boolean;
  confirm: boolean; // true: Geaccepteerd at once; false: quote with accept link
}

// booking-team answers with { error, message }; these read better in Dutch.
const TEAM_ERROR_NL: Record<string, string> = {
  slot_taken: 'Deze tijdsloten zijn niet (meer) helemaal vrij. Kies een andere starttijd.',
  not_open: 'Op dit moment is de locatie niet open voor deze groep.',
  too_soon: 'Deze datum is al voorbij.',
  location_not_enabled: 'Teamuitjes staan voor deze locatie niet aan.',
  group_size_out_of_range: 'Groepsgrootte moet tussen 1 en 500 liggen.',
  permission_denied: 'Je hebt geen recht op Teamuitjes.',
  unauthenticated: 'Je bent niet ingelogd.',
  invalid_transition: 'Deze aanvraag kan niet meer worden bevestigd (verlopen of al verwerkt).',
};

async function callTeam<T>(body: Record<string, unknown>): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  const jwt = session.session?.access_token;
  const res = await fetch(`${BOOKING_API_URL}/booking-team`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify(body),
  });
  let json: Record<string, unknown> = {};
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    // non-JSON: fall through to the HTTP status
  }
  if (!res.ok) {
    const code = typeof json.error === 'string' ? json.error : `HTTP ${res.status}`;
    const field = code === 'invalid_input' && typeof json.message === 'string' ? `: ${json.message}` : '';
    throw new Error(TEAM_ERROR_NL[code] ?? `${code}${field}`);
  }
  return json as T;
}

export async function fetchTeamConfig(): Promise<TeamLocationConfig[]> {
  const r = await callTeam<{ locations: TeamLocationConfig[] }>({ action: 'config' });
  return r.locations ?? [];
}

export async function fetchTeamSlots(locationId: string, date: string): Promise<TeamSlot[]> {
  const r = await callTeam<{ days: Array<{ date: string; slots: TeamSlot[] }> }>({
    action: 'slots', location_id: locationId, from: date, to: date,
  });
  return r.days?.[0]?.slots ?? [];
}

export async function createTeamQuote(input: TeamQuoteInput): Promise<TeamQuote> {
  const r = await callTeam<{ quote: TeamQuote }>({ action: 'admin_create', ...input });
  return r.quote;
}

export async function confirmTeamQuote(id: string): Promise<TeamQuote> {
  const r = await callTeam<{ quote: TeamQuote }>({ action: 'admin_accept', id });
  return r.quote;
}

// Start times (HH:MM, Europe/Amsterdam) where a group of `group` fits in whole
// free slots: in some room, ceil(group / capacity) consecutive 20-minute ticks
// from that start are all free. Same rule as fn_team_quote_create_core.
export function freeStarts(slots: TeamSlot[], group: number): string[] {
  const byRoom = new Map<string, { capacity: number; free: Set<number> }>();
  for (const s of slots) {
    const room = byRoom.get(s.room_id) ?? { capacity: s.capacity, free: new Set<number>() };
    if (s.free) room.free.add(Date.parse(s.slot_start));
    byRoom.set(s.room_id, room);
  }
  const starts = new Set<number>();
  for (const room of byRoom.values()) {
    const n = Math.ceil(group / room.capacity);
    for (const t of room.free) {
      let ok = true;
      for (let i = 1; i < n && ok; i++) ok = room.free.has(t + i * 20 * 60_000);
      if (ok) starts.add(t);
    }
  }
  const hm = new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return [...starts].sort((a, b) => a - b).map((t) => hm.format(new Date(t)));
}

export function euro(cents: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

// YYYY-MM-DD (a plain date) as "vr 20 nov 2026".
export function plainDate(ymd: string | null): string {
  if (!ymd) return '—';
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${ymd}T12:00:00Z`));
}

// Today in Europe/Amsterdam as YYYY-MM-DD.
export function venueToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// -----------------------------------------------------------------------------
// Calendar: team outing slots (booking.fn_calendar_team_outings, staff only)
// -----------------------------------------------------------------------------
// A team slot is blocked for everyone else (capacity 0 in fn_calendar_departures),
// so the calendar draws the team from this list instead.
export interface CalendarTeamSlot {
  quote_id: string;
  quote_number: string;
  status: TeamQuoteStatus;
  company: string;
  group_size: number;
  booking_number: string;
  room_id: string;
  location_id: string;
  slot_start: string;
  date: string;        // YYYY-MM-DD, Europe/Amsterdam
  time_of_day: string; // HH:MM, Europe/Amsterdam
  people: number;
  part: number;        // 1-based
  parts: number;
  expires_at: string;
}

export async function fetchCalendarTeamOutings(
  fromDate: string,
  toDate: string,
  locationId: string | null,
): Promise<CalendarTeamSlot[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_calendar_team_outings', {
      p_from: fromDate,
      p_to: toDate,
      p_location_id: locationId,
    });
  if (error) throw error;
  return Array.isArray(data) ? (data as CalendarTeamSlot[]) : [];
}
